import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { usePaymentModeOptions } from 'hooks/usePaymentModeOptions';
import { OutstandingDisplay } from 'components/common';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import {
  receiptService, coaLedgerService, partyService, currencyService, entityAccountingConfigService,
} from 'api/services';
import type {
  ReceiptDto, ReceiptDocStatus, ReceiptAssignmentStatus,
  CreateReceiptRequest, CreateReceiptAssignmentRequest, AssignReceiptRequest,
  AssignmentTargetDto,
} from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ============================================================================
// RECEIPT MODALS (ADR-0024 Slice ① Leg 1)
//   ReceiptFormModal   — capture (post-on-create) with the BU-assignment sub-grid
//   ReceiptViewModal   — read-only header + assignments
//   ReceiptAssignModal — assign the unassigned remainder / redistribute a
//                        RETURNED portion (POST /receipts/{id}/assign)
// ============================================================================

const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const EPS = 0.005;

/**
 * Instrument modes — mirrors the seeded enum common.tbl_enum_master
 * enum_group='BNK_INSTRUMENT_TYPE' (seed 117). No reference endpoint exposes
 * this group yet, so the options are declared here 1:1 with the seed.
 */
const INSTRUMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'EFT', label: 'EFT / Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash Deposit' },
  { value: 'DD', label: 'Demand Draft' },
  { value: 'CARD', label: 'Card' },
  { value: 'OTHER', label: 'Other' },
];

// ---- Status chips (shared by both banking screens + modals) ----

export const receiptStatusBadge = (s: ReceiptDocStatus): React.ReactNode => {
  const style: React.CSSProperties | undefined =
    s === 'PARTIALLY_ALLOCATED' ? { background: '#d97706', color: '#fff' }
    : s === 'CLOSED' ? { background: '#16a34a', color: '#fff' }
    : s === 'CANCELLED' ? { background: '#6b7280', color: '#fff' }
    : undefined;
  const label = s === 'PARTIALLY_ALLOCATED' ? 'Partially Allocated'
    : s === 'CLOSED' ? 'Closed'
    : s === 'CANCELLED' ? 'Cancelled'
    : 'Captured';
  return <span className="badge" style={style}>{label}</span>;
};

export const assignmentStatusBadge = (s: ReceiptAssignmentStatus): React.ReactNode => {
  const style: React.CSSProperties | undefined =
    s === 'POSTED' ? { background: '#16a34a', color: '#fff' }
    : s === 'RETURNED' ? { background: '#dc2626', color: '#fff' }
    : { background: '#d97706', color: '#fff' };
  const label = s === 'POSTED' ? 'Posted' : s === 'RETURNED' ? 'Returned' : 'Pending';
  return <span className="badge" style={style}>{label}</span>;
};

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const cellInput: React.CSSProperties = { width: '100%', fontSize: '0.8125rem', padding: '0.375rem 0.5rem' };

// ============================================================================
// BU ASSIGNMENT SUB-GRID — shared by the capture + assign modals.
// One row = one BU portion (CreateReceiptAssignmentRequest). ADR-0026: a BU may
// belong to ANOTHER group entity — the picker is entity-grouped and the row
// carries targetEntityId ('' = the receipt's own entity).
// ============================================================================

export interface AssignmentRow {
  key: number;
  /** '' = the receipt's own entity; another entity id = cross-entity routing (ADR-0026). */
  targetEntityId: string;
  businessUnitId: string;
  amount: string;
  partyId: string;
  partyLabel: string;
  partyHintText: string;
  remarks: string;
}

/** Encoded picker value: `${entityId}::${businessUnitId}`. */
const TARGET_SEP = '::';

/**
 * Entity-grouped BU options from /assignment-targets: the session/receipt
 * entity's BUs first (plain labels), other entities after ("Entity — BU").
 * Entities whose functional currency differs from the receipt currency are
 * disabled (server guard: cross-entity requires matching functional currency).
 */
const buildTargetOptions = (
  targets: AssignmentTargetDto[],
  ownEntityId: string,
  receiptCurrency: string,
  restrictToEntityId?: string,
): SelectOption[] => {
  const ordered = [...targets].sort((a, b) => {
    if (a.entityId === ownEntityId) return -1;
    if (b.entityId === ownEntityId) return 1;
    return (a.entityName || '').localeCompare(b.entityName || '');
  });
  const options: SelectOption[] = [];
  for (const t of ordered) {
    if (restrictToEntityId && t.entityId !== restrictToEntityId) continue;
    const isOwn = t.entityId === ownEntityId;
    const currencyMismatch = !isOwn && !!receiptCurrency && !!t.functionalCurrencyCode
      && t.functionalCurrencyCode.toUpperCase() !== receiptCurrency.toUpperCase();
    for (const bu of t.businessUnits || []) {
      options.push({
        value: `${t.entityId}${TARGET_SEP}${bu.businessUnitId}`,
        label: isOwn
          ? bu.unitName
          : `${t.entityName} — ${bu.unitName}${currencyMismatch ? ' (different functional currency — not routable in this version)' : ''}`,
        isDisabled: currencyMismatch,
      });
    }
  }
  return options;
};

/** Targets lookup shared by both modals (fetched per open; VIEW-gated). */
const useAssignmentTargets = (isOpen: boolean): AssignmentTargetDto[] => {
  const [targets, setTargets] = useState<AssignmentTargetDto[]>([]);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    receiptService.getAssignmentTargets()
      .then(t => { if (!cancelled) setTargets(t); })
      .catch(() => { /* picker falls back to empty — validation prompts */ });
    return () => { cancelled = true; };
  }, [isOpen]);
  return targets;
};

interface AssignmentRowsEditorProps {
  rows: AssignmentRow[];
  /** Encoded `${entityId}::${businessUnitId}` options (buildTargetOptions). */
  buOptions: SelectOption[];
  /** The receipt's own entity — rows targeting it carry targetEntityId ''. */
  ownEntityId: string;
  onUpdate: (key: number, patch: Partial<AssignmentRow>) => void;
  onAdd: () => void;
  onRemove: (key: number) => void;
  loadParties: (input: string) => Promise<SelectOption[]>;
  partyLabelCache: React.MutableRefObject<Map<string, string>>;
}

const AssignmentRowsEditor: React.FC<AssignmentRowsEditorProps> = ({
  rows, buOptions, ownEntityId, onUpdate, onAdd, onRemove, loadParties, partyLabelCache,
}) => (
  <>
    <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
      <thead>
        <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
          <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', minWidth: 170 }}>Business Unit</th>
          <th style={{ width: 130, textAlign: 'right', padding: '0.5rem' }}>Amount</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', minWidth: 180 }}>Customer (optional)</th>
          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Hint Text</th>
          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Remarks</th>
          <th style={{ width: 36, padding: '0.5rem' }} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={7} style={{ textAlign: 'center', padding: '0.75rem', color: 'var(--text-muted)' }}>
              No BU portions yet — the full amount stays unassigned at capture.
            </td>
          </tr>
        )}
        {rows.map((r, idx) => {
          // A (entity, BU) pair may appear only once — options already used by
          // OTHER rows are disabled.
          const encodedFor = (x: AssignmentRow) => (x.businessUnitId ? `${x.targetEntityId || ownEntityId}${TARGET_SEP}${x.businessUnitId}` : '');
          const rowValue = encodedFor(r);
          const rowBuOptions = buOptions.map(o => (
            rows.some(x => x.key !== r.key && encodedFor(x) === o.value)
              ? { ...o, isDisabled: true }
              : o
          ));
          return (
            <tr key={r.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
              <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
              <td style={{ padding: '0.25rem 0.5rem' }}>
                <SyutSelect options={rowBuOptions} value={rowValue} menuPortalTarget={document.body}
                  onChange={(v) => {
                    const enc = (v as string) || '';
                    if (!enc) { onUpdate(r.key, { businessUnitId: '', targetEntityId: '' }); return; }
                    const [entityId, buId] = enc.split(TARGET_SEP);
                    onUpdate(r.key, {
                      businessUnitId: buId || '',
                      targetEntityId: entityId && entityId !== ownEntityId ? entityId : '',
                    });
                  }}
                  placeholder="Pick a BU" isClearable size="sm" />
                {r.targetEntityId && (
                  <div style={{ marginTop: '2px' }}>
                    <span className="badge" style={{ background: '#0891b2', color: '#fff', fontSize: '0.68rem' }}
                      title="This portion routes to another group entity via the intercompany mirror">
                      ⇄ via intercompany
                    </span>
                  </div>
                )}
              </td>
              <td style={{ padding: '0.25rem 0.5rem' }}>
                <input type="number" inputMode="decimal" className="form-input" value={r.amount}
                  onChange={(e) => onUpdate(r.key, { amount: e.target.value })}
                  placeholder="0.00" style={{ ...cellInput, textAlign: 'right' }} />
              </td>
              <td style={{ padding: '0.25rem 0.5rem' }}>
                <SyutSelect isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                  seedOptions={r.partyId ? [{ value: r.partyId, label: r.partyLabel }] : undefined}
                  menuPortalTarget={document.body} value={r.partyId}
                  onChange={(v) => onUpdate(r.key, { partyId: (v as string) || '', partyLabel: (v && partyLabelCache.current.get(v as string)) || '' })}
                  placeholder="Customer hint" isClearable size="sm" />
              </td>
              <td style={{ padding: '0.25rem 0.5rem' }}>
                <input type="text" className="form-input" value={r.partyHintText}
                  onChange={(e) => onUpdate(r.key, { partyHintText: e.target.value })}
                  placeholder="e.g. payer as on statement" style={cellInput} />
              </td>
              <td style={{ padding: '0.25rem 0.5rem' }}>
                <input type="text" className="form-input" value={r.remarks}
                  onChange={(e) => onUpdate(r.key, { remarks: e.target.value })} style={cellInput} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <button title="Remove row" onClick={() => onRemove(r.key)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}>
                  <IconSystem.trash size={15} />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <div style={{ marginTop: '0.5rem' }}>
      <button className="btn btn-secondary" onClick={onAdd} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
        <IconSystem.add size={14} /> Add BU Portion
      </button>
    </div>
  </>
);

/** Rows with any user data (used for validation + payload building). */
const activeRows = (rows: AssignmentRow[]): AssignmentRow[] =>
  rows.filter(r => r.businessUnitId || num(r.amount) > 0 || r.partyId || r.partyHintText.trim() || r.remarks.trim());

/** Validate the shared BU-portion rows. Returns an error string or null. */
const validateRows = (rows: AssignmentRow[]): string | null => {
  const filled = activeRows(rows);
  if (filled.some(r => !r.businessUnitId)) return 'Every BU portion needs a business unit.';
  if (filled.some(r => num(r.amount) <= 0)) return 'Every BU portion needs a positive amount.';
  // Duplicate check per (entity, BU) — the same BU may legitimately repeat only
  // if it belonged to different entities (it can't, but the key is per-pair).
  const keys = filled.map(r => `${r.targetEntityId}${TARGET_SEP}${r.businessUnitId}`);
  if (new Set(keys).size !== keys.length) return 'A business unit may appear only once.';
  return null;
};

const rowsToRequests = (rows: AssignmentRow[]): CreateReceiptAssignmentRequest[] =>
  activeRows(rows).map(r => ({
    targetEntityId: r.targetEntityId || undefined,
    businessUnitId: r.businessUnitId,
    amount: num(r.amount),
    partyId: r.partyId || undefined,
    partyHintText: r.partyHintText.trim() || undefined,
    remarks: r.remarks.trim() || undefined,
  }));

// ============================================================================
// CAPTURE MODAL — posts the Bank Receipt voucher on save (create-only).
// ============================================================================

interface ReceiptForm {
  receiptDate: string;
  bankLedgerId: string;
  bankLedgerLabel: string;
  instrumentType: string;
  instrumentNo: string;
  instrumentDate: string;
  drawerBank: string;
  currencyCode: string;
  exchangeRate: string;
  amount: string;
  payerText: string;
  partyId: string;
  partyLabel: string;
  reference: string;
  narration: string;
}

/** Seed values applied on open (bank-statement create-receipt-from-line flow). */
export interface ReceiptFormPrefill {
  receiptDate?: string;
  bankLedgerId?: string;
  bankLedgerLabel?: string;
  amount?: number;
  payerText?: string;
  reference?: string;
  narration?: string;
}

export type ReceiptLockedField = 'receiptDate' | 'bankLedgerId' | 'amount';

export interface ReceiptFormModalProps {
  isOpen: boolean;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onRestoreModal?: (mode: 'create', instanceId?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /** Optional seed values applied when the modal opens (normal behavior when omitted). */
  prefill?: ReceiptFormPrefill;
  /** Fields rendered read-only (e.g. locked to a bank-statement line). */
  lockedFields?: ReceiptLockedField[];
  /** Replaces receiptService.create — receives the built payload, returns the saved receipt. */
  onSubmitOverride?: (payload: CreateReceiptRequest) => Promise<ReceiptDto>;
  /** Optional modal title (defaults to "New Receipt"). */
  titleOverride?: string;
}

export const ReceiptFormModal: React.FC<ReceiptFormModalProps> = ({
  isOpen, onSaved, onClose, tabId, moduleName, createInstanceId, onRestoreModal, minimizeRef,
  prefill, lockedFields, onSubmitOverride, titleOverride,
}) => {
  const { minimize } = useModalMinimize();
  const { selectedEntity, selectedBusinessUnit } = useAuthContext();
  const ownEntityId = selectedEntity?.id || '';
  const sessionBuId = selectedBusinessUnit?.id;
  const targets = useAssignmentTargets(isOpen);
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;

  const rowKey = useRef(0);
  const blankRow = useCallback((): AssignmentRow => ({
    key: ++rowKey.current, targetEntityId: '', businessUnitId: '', amount: '', partyId: '', partyLabel: '', partyHintText: '', remarks: '',
  }), []);
  const initialForm = useCallback((): ReceiptForm => ({
    receiptDate: today(), bankLedgerId: '', bankLedgerLabel: '', instrumentType: '', instrumentNo: '',
    instrumentDate: '', drawerBank: '', currencyCode: '', exchangeRate: '1', amount: '',
    payerText: '', partyId: '', partyLabel: '', reference: '', narration: '',
  }), []);

  const [form, setForm] = useState<ReceiptForm>(initialForm);

  // Payment modes come from the company-wide master (ADR-0055 §6); the static list is the fallback.

  const modeOptions = usePaymentModeOptions(INSTRUMENT_TYPE_OPTIONS, form.instrumentType);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const rowsRef = useRef(rows); rowsRef.current = rows;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const prefillRef = useRef(prefill); prefillRef.current = prefill;
  const onSubmitOverrideRef = useRef(onSubmitOverride); onSubmitOverrideRef.current = onSubmitOverride;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const partyLabelCache = useRef<Map<string, string>>(new Map());
  const ledgerLabelCache = useRef<Map<string, string>>(new Map());

  const isLocked = useCallback((f: ReceiptLockedField) => (lockedFields || []).includes(f), [lockedFields]);

  // Snapshot for dirty tracking — user-editable fields only (label caches excluded).
  const snapshot = useCallback((f: ReceiptForm, r: AssignmentRow[]) => JSON.stringify({
    f: { ...f, bankLedgerLabel: undefined, partyLabel: undefined },
    r: r.map(x => ({
      targetEntityId: x.targetEntityId, businessUnitId: x.businessUnitId, amount: x.amount, partyId: x.partyId,
      partyHintText: x.partyHintText, remarks: x.remarks,
    })),
  }), []);

  // Reset on open (skipped when restoring a minimized session). Prefill values
  // (create-from-statement-line flow) seed the fresh form and its snapshot.
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    const p = prefillRef.current;
    if (p?.bankLedgerId && p.bankLedgerLabel) ledgerLabelCache.current.set(p.bankLedgerId, p.bankLedgerLabel);
    const f: ReceiptForm = {
      ...initialForm(),
      receiptDate: defaultDateIn(fyRef.current),
      ...(p?.receiptDate ? { receiptDate: p.receiptDate } : {}),
      ...(p?.bankLedgerId ? { bankLedgerId: p.bankLedgerId, bankLedgerLabel: p.bankLedgerLabel || '' } : {}),
      ...(p?.amount !== undefined ? { amount: String(p.amount) } : {}),
      ...(p?.payerText ? { payerText: p.payerText } : {}),
      ...(p?.reference ? { reference: p.reference } : {}),
      ...(p?.narration ? { narration: p.narration } : {}),
    };
    const r: AssignmentRow[] = [];
    setForm(f);
    setRows(r);
    initialSnapshotRef.current = snapshot(f, r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Functional currency lookup (defaults the receipt currency).
  useEffect(() => {
    if (!isOpen) return;
    const cancelToken = { cancelled: false };
    entityAccountingConfigService.getConfig().then(cfg => {
      if (cancelToken.cancelled) return;
      const fc = cfg.functionalCurrencyCode || '';
      setFunctionalCurrency(fc);
      setForm(prev => {
        if (prev.currencyCode) return prev;
        // Rewrite the snapshot so the async default doesn't register as dirty.
        try {
          const snap = JSON.parse(initialSnapshotRef.current);
          if (snap?.f && !snap.f.currencyCode) {
            snap.f.currencyCode = fc;
            initialSnapshotRef.current = JSON.stringify(snap);
          }
        } catch { /* snapshot may be from a restored session */ }
        return { ...prev, currencyCode: fc };
      });
    }).catch(() => { /* currency stays unset; validation will prompt */ });
    return () => { cancelToken.cancelled = true; };
  }, [isOpen]);

  const loadBankLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      // ADR-0027 ③c: transaction pickers only offer ledgers available to the session BU.
      const res = await coaLedgerService.getAll({
        pageSize: 50, searchTerm: input || undefined, ledgerNature: 'BANK',
        availableToBusinessUnitId: sessionBuId,
      });
      res.data.forEach(l => ledgerLabelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, [sessionBuId]);

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined });
      r.data.forEach(p => partyLabelCache.current.set(p.id, `${p.partyCode} - ${p.partyName}`));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}` }));
    } catch {
      return [];
    }
  }, []);

  const loadCurrencies = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      return list.map(c => ({ value: c.currencyCode, label: `${c.currencyCode} - ${c.currencyName}` }));
    } catch {
      return [];
    }
  }, []);

  const updateForm = useCallback((patch: Partial<ReceiptForm>) => setForm(prev => ({ ...prev, ...patch })), []);
  const updateRow = useCallback((key: number, patch: Partial<AssignmentRow>) => setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r))), []);
  const addRow = useCallback(() => setRows(prev => [...prev, blankRow()]), [blankRow]);
  const removeRow = useCallback((key: number) => setRows(prev => prev.filter(r => r.key !== key)), []);

  const isForeign = !!form.currencyCode && !!functionalCurrency && form.currencyCode !== functionalCurrency;
  const amount = num(form.amount);
  const assignedTotal = useMemo(() => rows.reduce((s, r) => s + num(r.amount), 0), [rows]);
  const remainder = amount - assignedTotal;

  // Entity-grouped BU picker (ADR-0026): own entity first; other entities are
  // routable only when their functional currency matches the receipt currency.
  const receiptCurrencyForRouting = form.currencyCode || functionalCurrency;
  const buOptions = useMemo(
    () => buildTargetOptions(targets, ownEntityId, receiptCurrencyForRouting),
    [targets, ownEntityId, receiptCurrencyForRouting],
  );

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return snapshot(form, rows) !== initialSnapshotRef.current;
  }, [form, rows, snapshot]);

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const f = formRef.current;
    const r = rowsRef.current;
    const cId = createInstanceIdRef.current || '1';
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? snapshot(f, r) !== savedSnapshot : false;
    minimize({
      id: `${tabId}-form-modal-create-${cId}`,
      title: `New Receipt (${cId})`,
      moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(f);
        setRows(r);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.('create', cId);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, snapshot]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const validate = useCallback((): string | null => {
    if (!form.receiptDate) return 'Enter the receipt date.';
    if (!form.bankLedgerId) return 'Pick the bank ledger the money arrived in.';
    if (amount <= 0) return 'Enter a positive receipt amount.';
    if (isForeign && num(form.exchangeRate) <= 0) return 'Enter the exchange rate for a foreign-currency receipt.';
    const rowErr = validateRows(rows);
    if (rowErr) return rowErr;
    if (remainder < -EPS) return `BU portions total ${money(assignedTotal)} — more than the receipt amount ${money(amount)}.`;
    // Server guard, surfaced early: cross-entity routing requires a matching
    // functional currency (rows may predate a later currency change).
    const mismatched = activeRows(rows).find(r => {
      if (!r.targetEntityId) return false;
      const t = targets.find(x => x.entityId === r.targetEntityId);
      return !!t?.functionalCurrencyCode && !!receiptCurrencyForRouting
        && t.functionalCurrencyCode.toUpperCase() !== receiptCurrencyForRouting.toUpperCase();
    });
    if (mismatched) {
      const t = targets.find(x => x.entityId === mismatched.targetEntityId);
      return `${t?.entityName || 'A target entity'} has a different functional currency (${t?.functionalCurrencyCode || '?'}) — cross-entity routing needs a matching currency in this version.`;
    }
    return null;
  }, [form, amount, isForeign, rows, remainder, assignedTotal, targets, receiptCurrencyForRouting]);

  const handleSaveClick = useCallback(() => {
    const err = validate();
    if (err) { notificationService.toast.warning(err); return; }
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreateReceiptRequest = {
        receiptDate: form.receiptDate,
        bankLedgerId: form.bankLedgerId,
        instrumentType: form.instrumentType || undefined,
        instrumentNo: form.instrumentNo.trim() || undefined,
        instrumentDate: form.instrumentDate || undefined,
        drawerBank: form.drawerBank.trim() || undefined,
        currencyCode: form.currencyCode || undefined,
        exchangeRate: isForeign ? num(form.exchangeRate) : undefined,
        amount,
        payerText: form.payerText.trim() || undefined,
        partyId: form.partyId || undefined,
        reference: form.reference.trim() || undefined,
        narration: form.narration.trim() || undefined,
        assignments: rowsToRequests(rows),
      };
      const saved = onSubmitOverrideRef.current
        ? await onSubmitOverrideRef.current(payload)
        : await receiptService.create(payload);
      // mark clean so close doesn't prompt
      initialSnapshotRef.current = snapshot(form, rows);
      notificationService.toast.success(`Receipt ${saved.receiptNo || ''} captured and posted successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not capture the receipt.');
    } finally {
      setSaving(false);
    }
  }, [form, amount, isForeign, rows, onSaved, onClose, snapshot]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Posting…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{titleOverride || 'New Receipt'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {/* Header fields */}
          <div className="form-grid-4" style={{ alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Receipt Date <span className="text-danger">*</span></label>
              <FyDateInput className="form-input" value={form.receiptDate} disabled={isLocked('receiptDate')}
                onChange={v => updateForm({ receiptDate: v })} style={inputStyle} />
            </div>
            <div>
              <SyutSelect label={<>Bank <span className="text-danger">*</span></>} isAsync loadOptions={loadBankLedgers}
                defaultOptions cacheOptions={false} menuPortalTarget={document.body}
                seedOptions={form.bankLedgerId ? [{ value: form.bankLedgerId, label: form.bankLedgerLabel }] : undefined}
                value={form.bankLedgerId} isDisabled={isLocked('bankLedgerId')}
                onChange={(v) => updateForm({ bankLedgerId: (v as string) || '', bankLedgerLabel: (v && ledgerLabelCache.current.get(v as string)) || '' })}
                placeholder="Pick a bank ledger" isClearable size="sm" />
            </div>
            <div>
              <SyutSelect label="Instrument Type" options={modeOptions} menuPortalTarget={document.body}
                value={form.instrumentType} onChange={(v) => updateForm({ instrumentType: (v as string) || '' })}
                placeholder="e.g. EFT" isClearable size="sm" />
            </div>
            <div>
              <label style={labelStyle}>Instrument No</label>
              <input type="text" className="form-input" value={form.instrumentNo}
                onChange={(e) => updateForm({ instrumentNo: e.target.value })}
                placeholder="Cheque / UTR no" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Instrument Date</label>
              <input type="date" className="form-input" value={form.instrumentDate}
                onChange={(e) => updateForm({ instrumentDate: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Drawer Bank</label>
              <input type="text" className="form-input" value={form.drawerBank}
                onChange={(e) => updateForm({ drawerBank: e.target.value })}
                placeholder="Payer's bank" style={inputStyle} />
            </div>
            <div>
              <SyutSelect label="Currency" isAsync loadOptions={loadCurrencies} defaultOptions cacheOptions={false}
                menuPortalTarget={document.body}
                seedOptions={form.currencyCode ? [{ value: form.currencyCode, label: form.currencyCode }] : undefined}
                value={form.currencyCode}
                onChange={(v) => {
                  const code = (v as string) || '';
                  updateForm({ currencyCode: code, exchangeRate: !code || code === functionalCurrency ? '1' : form.exchangeRate });
                }}
                placeholder={functionalCurrency || 'Currency'} isClearable={false} size="sm" />
            </div>
            {isForeign ? (
              <div>
                <label style={labelStyle}>Exchange Rate <span className="text-danger">*</span></label>
                <input type="number" inputMode="decimal" className="form-input" value={form.exchangeRate}
                  onChange={(e) => updateForm({ exchangeRate: e.target.value })}
                  placeholder="1.000000" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>
            ) : <div />}
            <div>
              <label style={labelStyle}>Amount <span className="text-danger">*</span></label>
              <input type="number" inputMode="decimal" className="form-input" value={form.amount} disabled={isLocked('amount')}
                onChange={(e) => updateForm({ amount: e.target.value })}
                placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
            </div>
            <div>
              <label style={labelStyle}>Payer</label>
              <input type="text" className="form-input" value={form.payerText}
                onChange={(e) => updateForm({ payerText: e.target.value })}
                placeholder="Payer as on bank statement" style={inputStyle} />
            </div>
            <div>
              <SyutSelect label="Customer (hint)" isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                menuPortalTarget={document.body}
                seedOptions={form.partyId ? [{ value: form.partyId, label: form.partyLabel }] : undefined}
                value={form.partyId}
                onChange={(v) => updateForm({ partyId: (v as string) || '', partyLabel: (v && partyLabelCache.current.get(v as string)) || '' })}
                placeholder="If known" isClearable size="sm" />
              {/* Once a customer is named, what they owe — the sanity check on a receipt
                  amount, on the same scope terms as the allocation that follows (ADR-0042 §6). */}
              <div style={{ marginTop: '0.3rem' }}>
                <OutstandingDisplay partyId={form.partyId} side="receivable" variant="inline" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Reference</label>
              <input type="text" className="form-input" value={form.reference}
                onChange={(e) => updateForm({ reference: e.target.value })}
                placeholder="e.g. deposit slip ref" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>Narration</label>
            <input type="text" className="form-input" value={form.narration}
              onChange={(e) => updateForm({ narration: e.target.value })}
              placeholder="Receipt narration" style={inputStyle} />
          </div>

          {/* BU assignment sub-grid */}
          <div style={{ marginTop: '1rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.375rem' }}>
            BU Assignment (optional — the remainder stays unassigned; BUs of other group entities route via intercompany)
          </div>
          <AssignmentRowsEditor rows={rows} buOptions={buOptions} ownEntityId={ownEntityId}
            onUpdate={updateRow} onAdd={addRow} onRemove={removeRow}
            loadParties={loadParties} partyLabelCache={partyLabelCache} />

          {/* Live footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.25rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
            <span>Assigned total: <strong>{money(assignedTotal)}</strong></span>
            <span>
              Unassigned remainder:&nbsp;
              <strong style={{ color: remainder < -EPS ? '#dc2626' : remainder > EPS ? '#d97706' : '#16a34a' }}>
                {money(remainder)}
              </strong>
              {remainder < -EPS && ' (exceeds the receipt amount)'}
            </span>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving || amount <= 0 || remainder < -EPS}>
            <IconSystem.check size={16} /> Save Receipt
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title="Capture Receipt"
        message={`Capture and post this receipt of ${form.currencyCode || ''} ${money(amount)}${activeRows(rows).length ? ` with ${activeRows(rows).length} BU portion(s)` : ''}? The Bank Receipt voucher is posted on save.`}
        confirmLabel="Post" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL — read-only receipt (header + assignments with voucher numbers).
// ============================================================================

export interface ReceiptViewModalProps {
  isOpen: boolean;
  receipt: ReceiptDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (receiptNo: string) => void;
}

export const ReceiptViewModal: React.FC<ReceiptViewModalProps> = ({
  isOpen, receipt, onClose, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<ReceiptDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !receipt?.id) { setDetail(null); return; }
    setLoading(true);
    receiptService.getById(receipt.id)
      .then(setDetail)
      .catch(() => setDetail(receipt))
      .finally(() => setLoading(false));
  }, [isOpen, receipt]);

  // Entity names for cross-entity portions (ADR-0026) — fetched only when needed.
  const [entityNames, setEntityNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!isOpen || !detail) return;
    const hasCrossEntity = (detail.assignments || []).some(a => !!a.entityId && a.entityId !== detail.entityId);
    if (!hasCrossEntity) return;
    let cancelled = false;
    receiptService.getAssignmentTargets()
      .then(ts => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        ts.forEach(t => { map[t.entityId] = t.entityName; });
        setEntityNames(map);
      })
      .catch(() => { /* chips fall back to the plain BU name */ });
    return () => { cancelled = true; };
  }, [isOpen, detail]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !receipt) return;
    const key = receipt.receiptNo || receipt.id;
    minimize({
      id: `${tabId}-view-modal-${key}`,
      title: `View - ${receipt.receiptNo || 'receipt'}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(key),
    });
    onClose();
  }, [tabId, moduleName, receipt, minimize, onClose, onRestoreView]);

  if (!isOpen || !receipt) return null;
  const r = detail ?? receipt;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Receipt — {r.receiptNo || '—'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8125rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Receipt No</div><strong>{r.receiptNo || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div><strong>{fmtDate(r.receiptDate)}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Bank</div><strong>{r.bankLedgerName || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div>{receiptStatusBadge(r.docStatus)}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Instrument</div><strong>{[r.instrumentType, r.instrumentNo].filter(Boolean).join(' · ') || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Drawer Bank</div><strong>{r.drawerBank || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Payer / Customer</div><strong>{r.partyName || r.payerText || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Reference</div><strong>{r.reference || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Amount</div><strong>{money(r.fcAmount)} {r.currencyCode || ''}{r.exchangeRate && r.exchangeRate !== 1 ? ` @ ${r.exchangeRate}` : ''}</strong></div>
            <div>
              <div style={{ color: 'var(--text-muted)' }}>Unassigned</div>
              <strong style={{ color: r.unassignedFcAmount > EPS ? '#d97706' : 'inherit' }}>{money(r.unassignedFcAmount)}</strong>
            </div>
            <div><div style={{ color: 'var(--text-muted)' }}>Source</div><strong>{r.source || 'MANUAL'}</strong></div>
            <div style={{ gridColumn: 'span 1' }}><div style={{ color: 'var(--text-muted)' }}>Narration</div><strong>{r.narration || '—'}</strong></div>
          </div>

          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.375rem' }}>BU Assignments</div>
          <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Business Unit</th>
                <th style={{ width: 130, textAlign: 'right', padding: '0.5rem' }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Customer / Hint</th>
                <th style={{ width: 110, textAlign: 'left', padding: '0.5rem' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Voucher No</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading…</td></tr>}
              {!loading && (r.assignments || []).length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>No BU assignments — the full amount is unassigned.</td></tr>
              )}
              {!loading && (r.assignments || []).map((a, idx) => {
                // Cross-entity portion (ADR-0026): entity qualifier + IC indicator.
                const crossEntity = !!a.entityId && a.entityId !== r.entityId;
                const viaIc = crossEntity || !!a.icOutTransactionId;
                return (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {crossEntity
                      ? `${entityNames[a.entityId!] || 'Other entity'} · ${a.businessUnitName || '—'}`
                      : (a.businessUnitName || '—')}
                    {viaIc && (
                      <span title="via intercompany transfer" style={{ marginLeft: '0.35rem', color: '#0891b2', fontWeight: 700 }}>⇄</span>
                    )}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>{money(a.fcAmount)}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{a.partyName || a.partyHintText || '—'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{assignmentStatusBadge(a.assignmentStatus)}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{a.allocationTransactionNo || '—'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{a.assignmentStatus === 'RETURNED' && a.returnReason ? `Returned: ${a.returnReason}` : (a.remarks || '—')}</td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Captured{r.createdByName ? ` by ${r.createdByName}` : ''}{r.createdAt ? ` on ${new Date(r.createdAt).toLocaleString()}` : ''}.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// ASSIGN MODAL — assign the unassigned remainder, or redistribute a RETURNED
// portion (sourceAssignmentId), via POST /receipts/{id}/assign.
// ============================================================================

export interface ReceiptAssignModalProps {
  isOpen: boolean;
  receipt: ReceiptDto | null;
  onAssigned: () => void;
  onClose: () => void;
}

export const ReceiptAssignModal: React.FC<ReceiptAssignModalProps> = ({
  isOpen, receipt, onAssigned, onClose,
}) => {
  const targets = useAssignmentTargets(isOpen);
  const rowKey = useRef(0);
  const blankRow = useCallback((): AssignmentRow => ({
    key: ++rowKey.current, targetEntityId: '', businessUnitId: '', amount: '', partyId: '', partyLabel: '', partyHintText: '', remarks: '',
  }), []);

  const [detail, setDetail] = useState<ReceiptDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [sourceAssignmentId, setSourceAssignmentId] = useState('');
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const partyLabelCache = useRef<Map<string, string>>(new Map());

  // Fresh fetch on open — remainder / RETURNED portions / rowVersion must be current.
  useEffect(() => {
    if (!isOpen || !receipt?.id) { setDetail(null); setRows([]); setSourceAssignmentId(''); return; }
    setLoading(true);
    receiptService.getById(receipt.id)
      .then(d => {
        setDetail(d);
        const returned = (d.assignments || []).filter(a => a.assignmentStatus === 'RETURNED');
        setSourceAssignmentId(d.unassignedFcAmount > EPS ? '' : (returned[0]?.id || ''));
        setRows([]);
      })
      .catch((err: any) => {
        notificationService.toast.error(err?.response?.data?.message || 'Could not load the receipt.');
        setDetail(null);
      })
      .finally(() => setLoading(false));
  }, [isOpen, receipt]);

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined });
      r.data.forEach(p => partyLabelCache.current.set(p.id, `${p.partyCode} - ${p.partyName}`));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}` }));
    } catch {
      return [];
    }
  }, []);

  const updateRow = useCallback((key: number, patch: Partial<AssignmentRow>) => setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r))), []);
  const addRow = useCallback(() => setRows(prev => [...prev, blankRow()]), [blankRow]);
  const removeRow = useCallback((key: number) => setRows(prev => prev.filter(r => r.key !== key)), []);

  const returnedAssignments = useMemo(
    () => (detail?.assignments || []).filter(a => a.assignmentStatus === 'RETURNED'),
    [detail],
  );
  const sourceOptions: SelectOption[] = useMemo(() => {
    const opts: SelectOption[] = [];
    if (detail && detail.unassignedFcAmount > EPS) {
      opts.push({ value: '', label: `Unassigned remainder — ${money(detail.unassignedFcAmount)} ${detail.currencyCode || ''}` });
    }
    returnedAssignments.forEach(a => opts.push({
      value: a.id,
      label: `Returned portion — ${a.businessUnitName || 'BU'} ${money(a.fcAmount)}${a.returnReason ? ` (${a.returnReason})` : ''}`,
    }));
    return opts;
  }, [detail, returnedAssignments]);

  const redistributing = !!sourceAssignmentId;
  const sourceAssignment = returnedAssignments.find(a => a.id === sourceAssignmentId);
  const targetAmount = redistributing ? (sourceAssignment?.fcAmount ?? 0) : (detail?.unassignedFcAmount ?? 0);
  const assignedTotal = useMemo(() => rows.reduce((s, r) => s + num(r.amount), 0), [rows]);
  const remaining = targetAmount - assignedTotal;

  // Entity-grouped BU picker (ADR-0026). The receipt's own entity leads; other
  // entities are routable only with a matching functional currency. A RETURNED
  // portion redistributes only within its own target entity (server rule).
  const ownEntityId = detail?.entityId || receipt?.entityId || '';
  const sourceEntityId = redistributing ? (sourceAssignment?.entityId || ownEntityId) : undefined;
  const buOptions = useMemo(
    () => buildTargetOptions(targets, ownEntityId, detail?.currencyCode || '', sourceEntityId),
    [targets, ownEntityId, detail?.currencyCode, sourceEntityId],
  );
  const sourceEntityName = sourceEntityId
    ? (targets.find(t => t.entityId === sourceEntityId)?.entityName || '')
    : '';

  const isDirty = activeRows(rows).length > 0;
  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const validate = useCallback((): string | null => {
    if (!detail) return 'The receipt is still loading.';
    if (sourceOptions.length === 0) return 'Nothing to assign — no unassigned remainder or returned portion.';
    const filled = activeRows(rows);
    if (filled.length === 0) return 'Add at least one BU portion.';
    const rowErr = validateRows(rows);
    if (rowErr) return rowErr;
    if (redistributing) {
      // Server rule: a RETURNED portion redistributes only within its own target entity.
      if (sourceEntityId && filled.some(r => (r.targetEntityId || ownEntityId) !== sourceEntityId)) {
        return `A returned portion can only be redistributed within ${sourceEntityName || 'its own entity'}.`;
      }
      if (Math.abs(assignedTotal - targetAmount) > EPS) {
        return `Redistribution must exactly cover the returned amount ${money(targetAmount)} (now ${money(assignedTotal)}).`;
      }
    } else if (assignedTotal - targetAmount > EPS) {
      return `BU portions total ${money(assignedTotal)} — more than the unassigned remainder ${money(targetAmount)}.`;
    }
    return null;
  }, [detail, sourceOptions, rows, redistributing, assignedTotal, targetAmount, sourceEntityId, sourceEntityName, ownEntityId]);

  const handleAssignClick = useCallback(() => {
    const err = validate();
    if (err) { notificationService.toast.warning(err); return; }
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmAssign = useCallback(async () => {
    if (!detail) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: AssignReceiptRequest = {
        rowVersion: detail.rowVersion,
        sourceAssignmentId: sourceAssignmentId || undefined,
        assignments: rowsToRequests(rows),
      };
      const saved = await receiptService.assign(detail.id, payload);
      notificationService.toast.success(`Receipt ${saved.receiptNo || ''} assigned successfully.`);
      onAssigned();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not assign the receipt.');
    } finally {
      setSaving(false);
    }
  }, [detail, sourceAssignmentId, rows, onAssigned, onClose]);

  if (!isOpen || !receipt) return null;
  const r = detail ?? receipt;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {(saving || loading) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{saving ? 'Assigning…' : 'Loading…'}</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Assign Receipt — {r.receiptNo || '—'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {/* Receipt strip */}
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.8125rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '1rem' }}>
            <span><span style={{ color: 'var(--text-muted)' }}>Date </span><strong>{fmtDate(r.receiptDate)}</strong></span>
            <span><span style={{ color: 'var(--text-muted)' }}>Bank </span><strong>{r.bankLedgerName || '—'}</strong></span>
            <span><span style={{ color: 'var(--text-muted)' }}>Payer </span><strong>{r.partyName || r.payerText || '—'}</strong></span>
            <span><span style={{ color: 'var(--text-muted)' }}>Amount </span><strong>{money(r.fcAmount)} {r.currencyCode || ''}</strong></span>
            <span><span style={{ color: 'var(--text-muted)' }}>Unassigned </span><strong style={{ color: (detail?.unassignedFcAmount ?? 0) > EPS ? '#d97706' : 'inherit' }}>{money(detail?.unassignedFcAmount ?? r.unassignedFcAmount)}</strong></span>
          </div>

          {sourceOptions.length === 0 && !loading ? (
            <div style={{ color: 'var(--text-muted)', padding: '1.5rem', textAlign: 'center' }}>
              Nothing to assign — this receipt has no unassigned remainder and no returned portion.
            </div>
          ) : (
            <>
              <div className="form-grid-2" style={{ marginBottom: '0.75rem' }}>
                <div>
                  <SyutSelect label="Assign From" options={sourceOptions} menuPortalTarget={document.body}
                    value={sourceAssignmentId}
                    onChange={(v) => { setSourceAssignmentId((v as string) || ''); }}
                    isClearable={false} size="sm" />
                </div>
                <div style={{ alignSelf: 'end', fontSize: '0.8125rem', color: 'var(--text-secondary)', paddingBottom: '0.375rem' }}>
                  {redistributing
                    ? <>Redistributing a returned portion — the new portions must total exactly <strong>{money(targetAmount)}</strong>
                      {sourceEntityName ? <> and stay within <strong>{sourceEntityName}</strong> (a returned portion cannot change entity)</> : null}.</>
                    : <>Partial draws are allowed — anything left stays unassigned. BUs of other group entities route via intercompany.</>}
                </div>
              </div>

              <AssignmentRowsEditor rows={rows} buOptions={buOptions} ownEntityId={ownEntityId}
                onUpdate={updateRow} onAdd={addRow} onRemove={removeRow}
                loadParties={loadParties} partyLabelCache={partyLabelCache} />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.25rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                <span>Assigned total: <strong>{money(assignedTotal)}</strong></span>
                <span>
                  Remaining:&nbsp;
                  <strong style={{ color: remaining < -EPS ? '#dc2626' : redistributing && Math.abs(remaining) > EPS ? '#d97706' : remaining > EPS ? '#d97706' : '#16a34a' }}>
                    {money(remaining)}
                  </strong>
                </span>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAssignClick}
            disabled={saving || loading || sourceOptions.length === 0 || activeRows(rows).length === 0}>
            <IconSystem.check size={16} /> Assign
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title="Assign Receipt"
        message={redistributing
          ? `Redistribute the returned portion of ${money(targetAmount)} across ${activeRows(rows).length} BU portion(s)? The clearing re-tag voucher is posted now.`
          : `Assign ${money(assignedTotal)} of the unassigned remainder to ${activeRows(rows).length} BU portion(s)? The clearing re-tag voucher is posted now.`}
        confirmLabel="Assign" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmAssign} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved BU portions. Are you sure you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default ReceiptFormModal;
